/**
 * Pure helpers for the customer page's API access tab: the value a product's
 * API key field takes, a tenant's connection state, a key's webhook health
 * and whether its environment can rotate. The tab translates the results.
 */

export type KeyRole = 'active' | 'replacement' | 'disabled'
export type AgoUnit = 'now' | 'minutes' | 'hours' | 'days'
export interface Ago {
  unit: AgoUnit
  count: number
}
export interface TenantConnection {
  state: 'never' | 'connected' | 'idle'
  ago: Ago | null
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** "<client id>:<client secret>", or the bare client id when there is no secret (local dev). */
export function cgtApiKeyValue(clientId: string, secret: string | null): string {
  return secret ? `${clientId}:${secret}` : clientId
}

/** How long ago, in the largest whole unit (under a minute is "now"). */
export function ago(at: Date, now: Date): Ago {
  const ms = Math.max(0, now.getTime() - at.getTime())
  if (ms < MINUTE) return { unit: 'now', count: 0 }
  if (ms < HOUR) return { unit: 'minutes', count: Math.floor(ms / MINUTE) }
  if (ms < DAY) return { unit: 'hours', count: Math.floor(ms / HOUR) }
  return { unit: 'days', count: Math.floor(ms / DAY) }
}

/** Never called; called within the last day (connected); or not for longer (idle). */
export function tenantConnection(lastSeenAt: string | null, now: Date): TenantConnection {
  if (!lastSeenAt) return { state: 'never', ago: null }
  const at = new Date(lastSeenAt)
  return { state: now.getTime() - at.getTime() <= DAY ? 'connected' : 'idle', ago: ago(at, now) }
}

export function webhookHealth(w: { active: number; failing: number; lastDelivery: { status: string } | null }): 'ok' | 'failing' | 'none' {
  if (!w.active) return 'none'
  return w.failing > 0 ? 'failing' : 'ok'
}

/** Rotation needs exactly one active key and no rotation already in progress. */
export function canRotate(keys: { role: KeyRole | string }[]): boolean {
  return keys.filter((k) => k.role === 'active').length === 1 && !keys.some((k) => k.role === 'replacement')
}
